import {defineComponent, ref, inject, reactive, toRefs, watch, computed} from "vue";
import debounce from "lodash-es/debounce";
import {useKlipApiProxy} from '@/plugins/proxy-client';
import {EnvelopeOfICollectionOfOrganisation, IOrganisation} from '@/api/klip-api.proxy';
import {find, forEach, isEmpty, map} from 'lodash-es';

export interface ISelectedOrganisation {
    organisationId?: string;
    name?: string | undefined;
    parentId?: string | undefined;
    namePath?: string | undefined;
}

interface IKlSearchOrganisationOption {
    title: string;
    subtitle: string;
    value: string; // contains organisationId. This is the property passed to the
}

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlSearchOrganisation',
    emits: ['selected', 'update:modelValue'],
    props: {
        modelValue: {
            type: [Object as () => ISelectedOrganisation, Array as () => ISelectedOrganisation[]],
            required: false,
            default: undefined,
        },
        showId: {
            type: Boolean,
            default: false,
            required: false
        },
        onlyIncludeUnaOrganisations: {
            type: Boolean,
            default: false,
            required: false
        },
        onlyIncludeRootOrganisations: {
            type: Boolean,
            default: false,
            required: false
        },
        single: {
            type: Boolean,
            default: true,
            required: false
        }
    },
    setup(props, { emit }) {

        const klipApiProxy = useKlipApiProxy();

        const selectedOrganisationId = ref<string>();
        const organisationOptionsFetching = ref<boolean>(false);
        const organisationOptions = ref<IKlSearchOrganisationOption[]>([]);

        const mode = computed((): string => props.single ? 'single' : 'tags');

        const organisationsCache = new Map<string, IOrganisation>();

        const _onOrganisationSelect = (organisationId: string | string[]) => {
            if (isEmpty(organisationId)) {
                emit('selected', null);
                emit('update:modelValue', null);
                return;
            }

            if (props.single) {
                console.assert(typeof organisationId === 'string', 'single mode expects single selects');
                const selectedOrg = _createResultOrg(organisationId as string);

                emit('selected', selectedOrg);
                emit('update:modelValue', selectedOrg);
            }
            else {
                console.assert(typeof organisationId === 'object', 'single mode expects array selects');
                const selectedOrgs = map(organisationId as string[], (currentId: string) => _createResultOrg(currentId));

                emit('selected', selectedOrgs);
                emit('update:modelValue', selectedOrgs);
            }
        };

        const _createResultOrg = (orgId: string): ISelectedOrganisation => {
            if (!organisationsCache.has(orgId)) {
                console.warn(`onOrganisationSelect: organisation not found "${orgId}"`);
                return;
            }

            const selectedOrganisation = organisationsCache.get(orgId);
            const organisation: ISelectedOrganisation = {
                organisationId: selectedOrganisation.organisationId,
                name: selectedOrganisation.name,
                namePath: selectedOrganisation.namePath,
                parentId:  selectedOrganisation.parentId,
            }

            return organisation;
        }

        const debouncedOrganisationSearch = debounce((searchValue) => {
            if (!searchValue) {
                return;
            }

            organisationOptionsFetching.value = true;
            klipApiProxy.organisation_FilterOrganisationsByName(searchValue, props.onlyIncludeUnaOrganisations, props.onlyIncludeRootOrganisations)
                .then((filterResult: EnvelopeOfICollectionOfOrganisation) => {
                    // add to cache
                    // TODO: refactor by adding organisation object to option.value (instead of organisationId)
                    // > logged as bug in vl-design-system: object as value throws exception
                    filterResult.result.forEach((item: IOrganisation) => organisationsCache.set(item.organisationId, item));

                    organisationOptions.value = filterResult.result.map((item: IOrganisation) => {
                        const suffix = props.showId ? ` - GUID: ${item.organisationId}` : '';
                        return {
                            title: `${item.name}${suffix}`,
                            subtitle: item.namePath,
                            value: item.organisationId,
                        }
                    });
                }).finally(() => {
                    organisationOptionsFetching.value = false;
                });
        }, 400);

        const reset = () => {
            selectedOrganisationId.value = null;
        }

        const onOrganisationInputChange = (searchValue: string) => {
            debouncedOrganisationSearch(searchValue);
        };

        watch(
            selectedOrganisationId,
            (newValue: string | string[], oldValue: string) => {
                _onOrganisationSelect(newValue);
            },
            { immediate: false, deep: true });

        return {
            selectedOrganisationId,
            organisationOptions,
            organisationOptionsFetching,
            onOrganisationInputChange,
            reset,
            mode,
        };
    }
});
